import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService } from 'src/app/api/services/company.service';
import { MESSAGES } from 'src/app/core/constant/messages';
import { ShowErrorComponent } from 'src/app/shared/component/show-error/show-error.component';
import { Company } from 'src/app/api/fn/generals/company.model';
import { ToastService } from 'src/app/shared/service/toast.service';
import {
  DepartamentService,
  DistrictService,
  ProvinceService,
} from 'src/app/api/services';
import { BusinessLineService } from 'src/app/api/services/business-line.service';
import { LoadingButtonComponent } from 'src/app/shared/component/loading-button/loading-button.component';
import { NavButtonComponent } from 'src/app/shared/component/nav-button/nav-button.component';
import { InputTextComponent } from 'src/app/shared/component/input-text/input-text.component';
import { InputSelectComponent } from 'src/app/shared/component/input-select/input-select.component';
import { InputTextareaComponent } from 'src/app/shared/component/input-textarea/input-textarea.component';
import { LoadingComponent } from 'src/app/shared/component/loading/loading.component';
import { BaseFormComponent } from 'src/app/shared/form/base-form.component';
import { noExtraSpacesValidator } from 'src/app/shared/validator/extra-spaces.validator';

@Component({
  selector: 'app-user-company-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    LoadingButtonComponent,
    NavButtonComponent,
    InputTextComponent,
    InputSelectComponent,
    InputTextareaComponent,
    LoadingComponent,
  ],
  templateUrl: './user-company-edit.component.html',
})
export class UserCompanyEditComponent
  extends BaseFormComponent
  implements OnInit
{
  company: Company | null = null;
  companyForm!: FormGroup;
  loading: boolean = false;
  isLoading: boolean = true;

  departaments!: any[];
  districts!: any[];
  provinces!: any[];
  bussinessLines!: any[];

  constructor(
    private route: ActivatedRoute,
    private departamentService: DepartamentService,
    private provinceService: ProvinceService,
    private districtService: DistrictService,
    private bussinessLineService: BusinessLineService,
    private companyService: CompanyService,
    toastService: ToastService,
    private router: Router,
    private fb: FormBuilder
  ) {
    super(toastService);
  }

  ngOnInit(): void {
    this.buildForm();

    this.departamentService.departamentoGet().subscribe({
      next: (data) => {
        this.departaments = data.dto.departaments.map((item: any) => ({
          value: item.idDepartament,
          label: item.name,
        }));
      },
    });

    this.bussinessLineService.getBusinessLines().subscribe({
      next: (data) => {
        this.bussinessLines = data.dto.businessLines.map((item: any) => ({
          value: item.idBusinessLine,
          label: item.description,
        }));
      },
    });

    this.route.parent?.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.companyService.getCompanyById(id).subscribe({
          next: async (response) => {
            this.company = response.dto.company;

            this.companyForm.patchValue({
              idDepartament: this.company.idDepartament || '',
              idProvince: this.company.idProvince || '',
              idDistrict: this.company.idDistrict || '',
              idBusinessLine: this.company.idBusinessLine || '',
              commercialName: this.company?.commercialName || '',
              socialReason: this.company?.socialReason || '',
              ruc: this.company?.ruc || '',
              mission: this.company.mission || '',
              vision: this.company.vision || '',
              ourHistory: this.company.ourHistory || '',
              slogan: this.company.slogan || '',
              businessInformation: this.company.businessInformation || '',
              urlVideo: this.company.urlVideo || '',
              urlUbication: this.company.urlUbication || '',
            });
            if (this.company.idDepartament) {
              await this.loadProvinces(this.company.idDepartament);
              if (this.company.idProvince) {
                await this.loadDistricts(this.company.idProvince);
              }
            }
            this.setupCascadingSelects();

            this.isLoading = false;
          },
        });
      }
    });
  }

  buildForm(): void {
    this.companyForm = this.fb.group({
      commercialName: [
        this.company?.commercialName || '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          noExtraSpacesValidator,
        ],
      ],
      socialReason: [
        this.company?.socialReason || '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          noExtraSpacesValidator,
        ],
      ],
      ruc: [
        this.company?.ruc || '',
        [
          Validators.required,
          Validators.pattern(/^\d{11}$/),
          Validators.minLength(11),
          Validators.maxLength(11),
        ],
      ],
      mission: [
        this.company?.mission || '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(1000),
        ],
      ],
      vision: [
        this.company?.vision || '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(1000),
        ],
      ],
      ourHistory: [
        this.company?.ourHistory || '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(1000),
        ],
      ],
      slogan: [
        this.company?.slogan || '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(200),
        ],
      ],
      businessInformation: [
        this.company?.businessInformation || '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(1000),
        ],
      ],
      idBusinessLine: [this.company?.idBusinessLine || '', Validators.required],
      idDistrict: [this.company?.idDistrict || '', Validators.required],
      idDepartament: [this.company?.idDepartament || '', Validators.required],
      idProvince: [this.company?.idProvince || '', Validators.required],
    });
  }

  onSubmit(): void {
    if (!this.validateForm(this.companyForm)) return;

    this.loading = true;

    const formValue = this.companyForm.value;

    const payload = {
      idBusinessLine: formValue.idBusinessLine,
      idDistrict: formValue.idDistrict,
      commercialName: formValue.commercialName,
      socialReason: formValue.socialReason,
      ruc: formValue.ruc,
      mission: formValue.mission,
      vision: formValue.vision,
      ourHistory: formValue.ourHistory,
      businessInformation: formValue.businessInformation,
      slogan: formValue.slogan,
      urlVideo: 'https://example.com/video',
      urlUbication: 'https://maps.example.com',
      valuesProperty: null,
    };

    this.companyService
      .updateCompany(this.company?.idCompany || '', payload)
      .subscribe({
        next: (res) => {
          this.toastService.showMessages('success', [
            'Empresa actualizada correctamente',
          ]);
          this.router.navigate(['/user/company', this.company?.idCompany]);
          this.loading = false;
        },
        error: (err) => {
          this.toastService.showMessages(
            'error',
            err.error?.listMessage || ['Error al actualizar empresa']
          );
          this.loading = false;
        },
      });
  }

  loadProvinces(idDepartament: string): Promise<void> {
    return new Promise((resolve) => {
      this.provinceService
        .provinciaDepartamentoIdDepartamentGet({ idDepartament })
        .subscribe({
          next: (res) => {
            this.provinces = res?.dto?.provinces.map((item: any) => ({
              value: item.id,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  loadDistricts(idProvince: string): Promise<void> {
    return new Promise((resolve) => {
      this.districtService
        .distritoProvinciaIdProvinceGet({ idProvince })
        .subscribe({
          next: (res) => {
            this.districts = res?.dto?.districts.map((item: any) => ({
              value: item.idDistrict,
              label: item.name,
            }));
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  setupCascadingSelects(): void {
    this.companyForm
      .get('idDepartament')
      ?.valueChanges.subscribe((idDepartament) => {
        if (idDepartament) {
          this.loadProvinces(idDepartament).then(() => {
            this.companyForm.patchValue({ idProvince: '', idDistrict: '' });
          });
        } else {
          this.provinces = [];
          this.districts = [];
          this.companyForm.patchValue({ idProvince: '', idDistrict: '' });
        }
      });

    this.companyForm.get('idProvince')?.valueChanges.subscribe((idProvince) => {
      if (idProvince) {
        this.loadDistricts(idProvince).then(() => {
          this.companyForm.patchValue({ idDistrict: '' });
        });
      } else {
        this.districts = [];
        this.companyForm.patchValue({ idDistrict: '' });
      }
    });
  }

  get routeNav() {
    return `/user/company/${this.company?.idCompany}`;
  }
}
